/**
 * * Extracts a human-readable error message from an RTK Query rejection.
 *
 * * The Moveris API error envelope looks like:
 * * {
 * *   status: number,
 * *   data: {
 * *     success: false,
 * *     message: "Validation failed",
 * *     errors: [{ field: string, detail: string | string[] }],
 * *     code: string
 * *   }
 * * }
 *
 * * Priority:
 * * 1. Field-level detail strings from `errors[]` — most specific
 * * 2. Top-level `message` — general description
 * * 3. Provided `fallback` — last resort
 */

export interface ServerErrorDetail {
  field: string;
  detail: string | string[];
}

interface ServerErrorBody {
  message?: string;
  errors?: ServerErrorDetail[];
  code?: string;
}

function getErrorBody(err: unknown): ServerErrorBody | undefined {
  if (!err || typeof err !== 'object' || !('data' in err)) return undefined;
  return (err as { data?: ServerErrorBody | null }).data ?? undefined;
}

export function extractServerError(err: unknown, fallback: string): string {
  const body = getErrorBody(err);
  if (!body) return fallback;

  if (Array.isArray(body.errors) && body.errors.length > 0) {
    const details = body.errors.flatMap(({ detail }) =>
      Array.isArray(detail) ? detail : [detail],
    );
    if (details.length > 0) return details.join(' ');
  }

  return body.message ?? fallback;
}

/** The envelope's machine-readable `code` (e.g. `ERR_ACCOUNT_NOT_ACTIVATED`), if any. */
export function extractServerErrorCode(err: unknown): string | undefined {
  const code = getErrorBody(err)?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The envelope's field-level `errors[]`, for mapping server errors onto form fields. */
export function extractServerFieldErrors(err: unknown): ServerErrorDetail[] {
  const errors = getErrorBody(err)?.errors;
  return Array.isArray(errors) ? errors : [];
}
